import type { Rng } from '../../../core/rng/Rng';
import { STEP } from '../../../core/GameLoop';
import { TAU } from '../../../core/math';
import type { BulletPool } from '../BulletPool';
import { Mod } from '../BulletPool';
import type { TelegraphSystem } from '../../combat/TelegraphSystem';
import type { AngleSpec, CompiledEmitter, CompiledPattern } from './schema';

/** What emitters need from the world each tick. */
export interface PatternContext {
  playerX: number;
  playerY: number;
  bullets: BulletPool;
  telegraphs: TelegraphSystem;
}

/**
 * Interprets one compiled pattern from a host position. All timing is integer
 * step equality. Randomness comes only from the injected per-emitter rng
 * stream; aimed angles read the player and consume no rng (the determinism
 * boundary: seeded shapes/timings, reactive aim).
 */
export class PatternInstance {
  private step = -1;
  /** Angles resolved at telegraph time, committed to at fire time. */
  private pendingAngles = new Map<CompiledEmitter, Map<number, number>>();

  constructor(
    private readonly pattern: CompiledPattern,
    private readonly rng: Rng,
  ) {}

  get done(): boolean {
    return this.step >= this.pattern.durationSteps;
  }

  /** Call once per sim tick with the host's current position. */
  tick(ctx: PatternContext, hostX: number, hostY: number): void {
    this.step++;
    if (this.done) return;
    for (const e of this.pattern.emitters) {
      this.tickEmitter(e, ctx, hostX, hostY);
    }
  }

  private originOf(e: CompiledEmitter, hostX: number, hostY: number, elapsed: number): [number, number] {
    if (e.originOffset) return [hostX + e.originOffset[0], hostY + e.originOffset[1]];
    if (e.orbit) {
      const a = elapsed * e.orbit.rateStep;
      return [hostX + Math.cos(a) * e.orbit.radius, hostY + Math.sin(a) * e.orbit.radius];
    }
    return [hostX, hostY];
  }

  private tickEmitter(e: CompiledEmitter, ctx: PatternContext, hostX: number, hostY: number): void {
    const elapsed = this.step - e.delaySteps;

    // Telegraph scheduling: spawn `leadSteps` before the volley it warns about.
    if (e.telegraph) {
      const tElapsed = elapsed + e.leadSteps;
      if (tElapsed >= 0 && tElapsed % e.intervalSteps === 0) {
        const k = tElapsed / e.intervalSteps;
        if (k < e.volleys) this.spawnTelegraph(e, k, ctx, hostX, hostY, tElapsed);
      }
    }

    if (elapsed < 0 || elapsed % e.intervalSteps !== 0) return;
    const k = elapsed / e.intervalSteps;
    if (k >= e.volleys) return;
    this.fireVolley(e, k, ctx, hostX, hostY, elapsed);
  }

  private spawnTelegraph(
    e: CompiledEmitter, volley: number, ctx: PatternContext,
    hostX: number, hostY: number, elapsed: number,
  ): void {
    const t = e.telegraph!;
    const [ox, oy] = this.originOf(e, hostX, hostY, elapsed);
    if (t.kind === 'zone') {
      const x = t.aimed ? ctx.playerX : ox;
      const y = t.aimed ? ctx.playerY : oy;
      ctx.telegraphs.spawnZone(x, y, t.radius ?? 80, e.leadSteps, t.damage ?? 1);
    } else {
      // Commit the volley to the telegraphed angle: resolve now, store for fire.
      const angle = this.resolveAngle(e.angle, e, ctx, ox, oy, elapsed);
      let m = this.pendingAngles.get(e);
      if (!m) {
        m = new Map();
        this.pendingAngles.set(e, m);
      }
      m.set(volley, angle);
      ctx.telegraphs.spawnLine(ox, oy, angle, t.length ?? 900, t.width ?? 40, e.leadSteps);
    }
  }

  private resolveAngle(
    spec: AngleSpec, e: CompiledEmitter, ctx: PatternContext,
    ox: number, oy: number, elapsed: number,
  ): number {
    switch (spec.kind) {
      case 'fixed':
        return spec.angle;
      case 'spin':
        return spec.start + spec.rate * elapsed * STEP;
      case 'aimed':
        return Math.atan2(ctx.playerY - oy, ctx.playerX - ox) + (spec.offset ?? 0);
      case 'randomArc':
        return (
          this.resolveAngle(spec.center, e, ctx, ox, oy, elapsed) +
          (this.rng.next() - 0.5) * spec.arc
        );
    }
  }

  private fireVolley(
    e: CompiledEmitter, volley: number, ctx: PatternContext,
    hostX: number, hostY: number, elapsed: number,
  ): void {
    const b = e.bullet;
    if (!b) return; // telegraph-only emitter (ground slam)

    const [ox, oy] = this.originOf(e, hostX, hostY, elapsed);
    const stored = this.pendingAngles.get(e)?.get(volley);
    if (stored !== undefined) this.pendingAngles.get(e)!.delete(volley);
    const base = stored ?? this.resolveAngle(e.angle, e, ctx, ox, oy, elapsed);

    const shot = e.shot;
    const arms = shot?.arms ?? 1;
    const armsArc = shot?.armsArc ?? TAU;
    const spreadCount = shot?.spreadCount ?? 1;
    const spread = shot?.spread ?? 0;
    const jitter = shot?.jitter ?? 0;

    // Gap arcs for this volley (dodge holes in walls/rings).
    let gapCenters: number[] | null = null;
    let gapWidth = 0;
    if (shot?.gap) {
      const g = shot.gap;
      gapWidth = g.width;
      let first: number;
      if (g.placement === 'random') {
        first = base + (this.rng.next() - 0.5) * (spreadCount > 1 ? spread : armsArc);
      } else if (g.placement === 'aimedSafe') {
        first = Math.atan2(ctx.playerY - oy, ctx.playerX - ox);
      } else {
        first = base + (g.at ?? 0);
      }
      gapCenters = [];
      for (let i = 0; i < g.count; i++) {
        gapCenters.push(first + (i * TAU) / g.count);
      }
    }

    for (let a = 0; a < arms; a++) {
      const armAngle = base + (armsArc === TAU ? (a * TAU) / arms : (arms > 1 ? -armsArc / 2 + (armsArc * a) / (arms - 1) : 0));
      for (let s = 0; s < spreadCount; s++) {
        let angle = armAngle + (spreadCount > 1 ? -spread / 2 + (spread * s) / (spreadCount - 1) : 0);
        if (jitter > 0) angle += (this.rng.next() - 0.5) * 2 * jitter;

        if (gapCenters) {
          let inGap = false;
          for (const gc of gapCenters) {
            let d = (angle - gc) % TAU;
            if (d > Math.PI) d -= TAU;
            if (d < -Math.PI) d += TAU;
            if (Math.abs(d) < gapWidth / 2) {
              inGap = true;
              break;
            }
          }
          if (inGap) continue;
        }

        const i = ctx.bullets.spawn(ox, oy, angle, b.speed, b.kind, b.radius, b.lifeSteps, b.scale);
        if (i >= 0 && b.modKind !== Mod.None) {
          ctx.bullets.setMod(i, b.modKind, b.p0, b.p1, b.p2);
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Phase composition: parallel tracks of sequenced/looped pattern refs.

export type TrackStep =
  | { pattern: string }
  | { wait: number }
  | { loop: TrackStep[]; times?: number };

interface TrackFrame {
  steps: TrackStep[];
  idx: number;
  timesLeft: number; // Infinity for forever-loops
}

/**
 * Runs named patterns in parallel tracks. Each track is a sequence: a pattern
 * step blocks until that pattern finishes, `wait` blocks for N seconds worth
 * of ticks, `loop` repeats its body. Used by boss phases and stress turrets.
 */
export class PhaseRunner {
  private tracks: { stack: TrackFrame[]; active: PatternInstance | null; waitLeft: number }[];

  constructor(
    private readonly registry: ReadonlyMap<string, CompiledPattern>,
    tracks: TrackStep[][],
    private readonly rng: Rng,
    private readonly toStepsFn: (seconds: number) => number,
  ) {
    this.tracks = tracks.map((steps) => ({
      stack: [{ steps, idx: 0, timesLeft: 1 }],
      active: null,
      waitLeft: 0,
    }));
  }

  tick(ctx: PatternContext, hostX: number, hostY: number): void {
    for (const track of this.tracks) {
      if (track.active) {
        track.active.tick(ctx, hostX, hostY);
        if (!track.active.done) continue;
        track.active = null;
      }
      if (track.waitLeft > 0) {
        track.waitLeft--;
        continue;
      }
      this.advance(track, ctx, hostX, hostY);
    }
  }

  private advance(
    track: { stack: TrackFrame[]; active: PatternInstance | null; waitLeft: number },
    ctx: PatternContext, hostX: number, hostY: number,
  ): void {
    // Bounded iterations to consume zero-length steps without infinite loops.
    for (let guard = 0; guard < 64; guard++) {
      const frame = track.stack[track.stack.length - 1];
      if (!frame) return; // sequence exhausted
      if (frame.idx >= frame.steps.length) {
        frame.timesLeft--;
        if (frame.timesLeft > 0) {
          frame.idx = 0;
          continue;
        }
        track.stack.pop();
        continue;
      }
      const step = frame.steps[frame.idx]!;
      if ('wait' in step) {
        frame.idx++;
        track.waitLeft = this.toStepsFn(step.wait);
        if (track.waitLeft > 0) return;
        continue;
      }
      if ('loop' in step) {
        frame.idx++;
        track.stack.push({
          steps: step.loop,
          idx: 0,
          timesLeft: step.times ?? Number.POSITIVE_INFINITY,
        });
        continue;
      }
      const compiled = this.registry.get(step.pattern);
      if (!compiled) throw new Error(`Unknown pattern: ${step.pattern}`);
      frame.idx++;
      track.active = new PatternInstance(compiled, this.rng);
      track.active.tick(ctx, hostX, hostY); // start this tick, not next
      return;
    }
  }
}
